import React from 'react';
import { useHistory } from 'react-router-dom';
import { ANONYMITY_OFF, ANONYMITY_ON, PRIVACY_PRIVATE, PRIVACY_PUBLIC } from 'utils/Constants';
import { AuthDialog } from 'components/AuthDialog/AuthDialog';
import { changeNewSpot, clearAddSpot, submitNewSpot } from 'store/slices/newSpot';
import Grid from '@mui/material/Unstable_Grid2';
import { Box, Button, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAutoSaveDraft } from 'hooks/useAutoSaveDraft';

interface CustomToggleButtonProps {
    value: string,
    copy: string,
    description: string
}

const CustomToggleButton = ({
    value,
    copy,
    description
}: CustomToggleButtonProps) =>
    <ToggleButton
        value={value}
        sx={{
            py: 3
        }}
    >
        <Box>
            <Typography
                variant={'subtitle1'}
                mb={1}
                sx={{
                    fontSize: {
                        xs: '.8em',
                        sm: '1em'
                    },
                    lineHeight: '1em'
                }}
            >
                {copy}
            </Typography>
            <Typography
                variant={'body1'}
                sx={{
                    textTransform: 'none',
                    fontSize: {
                        xs: '13px',
                        sm: '15px',
                    },
                    px: {
                        xs: 1,
                        sm: 2
                    }
                }}
            >{description}</Typography>
        </Box>
    </ToggleButton>

const AddSpotStep4 = () => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { save } = useAutoSaveDraft();
    const newSpot = useAppSelector((state) => state.newSpot);
    const { privacy, anonymity } = newSpot;

    const setPrivacy = (privacy: string) => {
        void dispatch(changeNewSpot({ privacy }));
    };

    const setAnonymity = (anonymity: string) => {
        void dispatch(changeNewSpot({ anonymity }));
    };

    return (
        <AuthDialog
            title={'Create a New Spot'}
            onClose={() => {
                save(4);
                dispatch(clearAddSpot());
                history.push('/');
            }}
            onBack={() => {
                save(4);
                history.push('/add-spot/step-3');
            }}
        >
            <form>
                <Grid container>
                    <Grid xs={12}>
                        <Typography variant={'body1'} mb={1}>
                            Do you want to share this spot with the KwadHub community?
                        </Typography>
                    </Grid>
                    <Grid xs={12}>
                        <ToggleButtonGroup
                            fullWidth
                            color='primary'
                            value={privacy}
                            exclusive
                            onChange={(_, newValue: string) => {
                                if (newValue) {
                                    setPrivacy(newValue);
                                }
                            }}
                            aria-label='Platform'
                        >
                            <CustomToggleButton
                                value={PRIVACY_PRIVATE}
                                copy={'Keep It Private'}
                                description={'Only you can see this spot for now. ' +
                                    'You can share it with the community whenever you\'re ready.'}
                            />
                            <CustomToggleButton
                                value={PRIVACY_PUBLIC}
                                copy={'Share with Community'}
                                description={'Your spot will go through an approval process and, ' +
                                    'once approved, will be visible to the KwadHub community.'}
                            />
                        </ToggleButtonGroup>
                    </Grid>
                    {privacy === PRIVACY_PUBLIC ? (
                        <Grid xs={12}>
                            <Box mt={3} mb={1}>
                                <Typography variant={'body1'}>
                                    Do you want to display your name as the submitter, or stay anonymous?
                                </Typography>
                            </Box>
                            <Box mb={4}>
                                <ToggleButtonGroup
                                    fullWidth
                                    color='primary'
                                    value={anonymity}
                                    exclusive
                                    onChange={(_, newValue: string) => {
                                        if (newValue) {
                                            setAnonymity(newValue);
                                        }
                                    }}
                                    aria-label='Platform'
                                >
                                    <CustomToggleButton
                                        value={ANONYMITY_OFF}
                                        copy={'Stay Anonymous'}
                                        description={'Your spot will be public, but your name won\'t appear. ' +
                                            'Note: If you\'ve added a YouTube link, ' +
                                            'viewers may still see your channel name.'}
                                    />
                                    <CustomToggleButton
                                        value={ANONYMITY_ON}
                                        copy={'Show My Name'}
                                        description={'Your name will be displayed as the spot\'s submitter, ' +
                                            'letting the community know it\'s your contribution!'}
                                    />
                                </ToggleButtonGroup>
                            </Box>
                        </Grid>
                    ) : null}
                </Grid>
                <Box
                    sx={{
                        width: '100%',
                        textAlign: 'center',
                        pt: 2,
                        mt: 4
                    }}
                >
                    <Button
                        variant='contained'
                        color={'primary'}
                        onClick={() => {
                            save(4);
                            void dispatch(submitNewSpot());
                            history.push('/add-spot/finished');
                        }}
                    >
                        Submit Spot
                    </Button>
                </Box>
            </form>
        </AuthDialog>
    );
};

export default AddSpotStep4;
